if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      const init = this.initLazySection.bind(this);
      if (window.initLazyScript) {
        window.initLazyScript(this, init);
      } else {
        requestAnimationFrame(init);
      }
    }

    initLazySection() {
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.indicatorBar = this.querySelector('.js-inventory-indicator');

      this.opts = {
        showNotice: this.dataset.showNotice || 'always',              // 'always' | 'low'
        showCount: this.dataset.showCount || 'low',                   // 'always' | 'low'
        showNoStockBackordered: this.dataset.showNoStockBackordered !== 'false',
        thresholdVeryLow: Number(this.dataset.thresholdVeryLow ?? 3),
        thresholdLow: Number(this.dataset.thresholdLow ?? 10),
        scale: Math.max(1, Number(this.dataset.scale ?? 20))
      };

      const s = (window.theme && window.theme.strings) || {};
      this.strings = {
        onlyXLeft: s.onlyXLeft || 'Only [quantity] left!',
        lowStock:  s.lowStock  || 'Low stock',
        inStock:   s.inStock   || 'In stock',
        noStock:   s.noStock   || 'Out of stock'
      };

      this.variantInventory = this.getVariantInventory();

      const variantPicker = this.closest('.product-info')?.querySelector('variant-picker');
      if (variantPicker) {
        ['on:variant:change', 'variant:change'].forEach((ev) =>
          variantPicker.addEventListener(ev, this.handleVariantChange.bind(this))
        );
      }

      const initialCount = Number(this.dataset.inventoryQuantity ?? 0);
      const initialAvailable = this.dataset.variantAvailable === 'true';
      const initialPolicy = this.dataset.inventoryPolicy || 'deny';

      this.updateInventory(initialCount, initialAvailable, initialPolicy);
    }

    getVariantInventory() {
      if (this.variantInventory) return this.variantInventory;
      const dataEl = this.querySelector('script[type="application/json"]');
      if (!dataEl || !dataEl.textContent.trim()) return [];
      try {
        const parsed = JSON.parse(dataEl.textContent);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.warn('Inventory JSON parse error:', e);
        return [];
      }
    }

    handleVariantChange(evt) {
      const v = evt?.detail?.variant;
      if (v) {
        const inventory = this.variantInventory.find((x) => String(x.id) === String(v.id));
        if (inventory) {
          this.updateInventory(
            Number(inventory.inventory_quantity ?? 0),
            Boolean(inventory.available),
            inventory.inventory_policy || 'deny'
          );
        } else {
          this.updateInventory(0, Boolean(v.available), v.inventory_policy || 'deny');
        }
      } else {
        this.updateInventory(0, false, 'deny');
        this.hidden = true;
      }
    }

    updateInventory(countRaw, available, inventoryPolicy) {
      const count = Number.isFinite(countRaw) ? countRaw : 0;
      let level;

      if (count <= 0) {
        if (inventoryPolicy === 'continue') {
          level = 'backordered';
        } else if (available) {
          level = 'in_stock';
        } else {
          level = 'none';
        }
      } else if (count <= this.opts.thresholdVeryLow) {
        level = 'very_low';
      } else if (count <= this.opts.thresholdLow) {
        level = 'low';
      } else {
        level = 'normal';
      }

      const shouldShow =
        (this.opts.showNotice === 'always' ||
          (this.opts.showNotice === 'low' && (level.includes('low') || level === 'none'))) &&
        !(level === 'backordered' && this.opts.showNoStockBackordered === false);

      if (!shouldShow) {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      this.setAttribute('data-inventory-level', level);

      if (this.inventoryNotice) {
        if (level === 'backordered') {
          this.inventoryNotice.innerText = '';
        } else if (level !== 'in_stock' &&
                   (this.opts.showCount === 'always' ||
                    (this.opts.showCount === 'low' && level.includes('low')))) {
          this.inventoryNotice.innerText = this.strings.onlyXLeft.replace('[quantity]', String(count));
        } else if (level.includes('low')) {
          this.inventoryNotice.innerText = this.strings.lowStock;
        } else if (level === 'normal' || level === 'in_stock') {
          this.inventoryNotice.innerText = this.strings.inStock;
        } else if (level === 'none') {
          this.inventoryNotice.innerText = this.strings.noStock;
        }
      }

      if (this.urgencyMessage) {
        if (level === 'very_low') {
          this.urgencyMessage.innerHTML = this.dataset.textVeryLow || '';
        } else if (level === 'low') {
          this.urgencyMessage.innerHTML = this.dataset.textLow || '';
        } else if (level === 'backordered') {
          this.urgencyMessage.innerHTML = this.dataset.textNoStockBackordered || '';
        } else if (level === 'normal' || level === 'in_stock') {
          this.urgencyMessage.innerHTML = this.dataset.textNormal || '';
        } else if (level === 'none') {
          this.urgencyMessage.innerHTML = this.dataset.textNoStock || '';
        }
      }

      if (this.indicatorBar) {
        this.indicatorBar.hidden = false;
        const span = this.indicatorBar.querySelector('span');
        if (span) {
          let newWidth;
          if ((count >= this.opts.scale || level === 'in_stock') && level !== 'backordered') {
            newWidth = 100;
          } else {
            newWidth = Math.max(0, Math.min(100, (100 / this.opts.scale) * count));
          }
          span.style.width = `${newWidth.toFixed(1)}%`;
        }
      }
    }
  }

  customElements.define('product-inventory', ProductInventory);
}
